import { Dispatch } from 'redux';

import type { GeoJSONFeature } from '@tmapy/mapcore';
import { actionDeleteAllFeatures } from '@tmapy/mapcore';
import { batch, ThunkAction } from '@tmapy/redux';

import type {
  CadasterListResponse,
  KrajListResponse,
  MomcListResponse,
  ObecListResponse,
  OrderEditRequest,
  OrdersPublishsetEditResponse,
  ORPListResponse,
} from '../../openapi/types';
import type { OrderTypeEnum } from '../../openapi/types/OrderTypeEnum';
import type { OriginEnum } from '../../openapi/types/OriginEnum';
import { OrderSelectionTypesEnum } from '../../openapi/types/OrderSelectionTypesEnum';

import type { ServerErrors } from '../../types';
import { ORDER_IS_PUBLIC, QUERY_STATE } from '../../constants';
import { actionResourceLoad } from '../../redux/resource/actions';
import { actionSetPredefinedAreasFeatures } from '../../areas/actions';

import * as con from '../constants';
import { UNMANAGED_AREA_LAYERS } from '../../areas/constants';

export type ActionNewOrderClear = ReturnType<typeof newOrderClear>;

export type PublishsetFormatPayload = { publishsetUrl: string; formatUrl: string };
export type PublishsetRemoveAllFormatsPayload = { publishsetUrl: string };
export type PublishsetSetAllFormatsPayload = {
  publishsetUrl: string;
  formatsUrl: string[];
};

export const actionSetCadasters = (cadasters: CadasterListResponse[]) => async (dispatch: any) => {
  dispatch({
    type: con.NEW_ORDER_SET_CADASTER,
    cadasters,
  });
  dispatch(actionSyncGeometries());
};

export const actionSetCities = (cities: ObecListResponse[]) => async (dispatch: any) => {
  dispatch({
    type: con.NEW_ORDER_SET_CITIES,
    cities,
  });
  dispatch(actionSyncGeometries());
};

export const actionSetORPs = (orps: ORPListResponse[]) => async (dispatch: any) => {
  dispatch({
    type: con.NEW_ORDER_SET_ORPS,
    orps,
  });
  dispatch(actionSyncGeometries());
};

export const actionSetRegions = (regions: KrajListResponse[]) => async (dispatch: any) => {
  dispatch({
    type: con.NEW_ORDER_SET_REGIONS,
    regions,
  });
  dispatch(actionSyncGeometries());
};

export const actionSetMomcs = (momcs: MomcListResponse[]) => async (dispatch: any) => {
  dispatch({
    type: con.NEW_ORDER_SET_MOMCS,
    momcs,
  });
  dispatch(actionSyncGeometries());
};

const actionLoadMissingGeometries = (): ThunkAction => async (dispatch, getState) => {
  const state = getState();
  const { momcs, orps, cadasters, cities, regions } = state.newOrder;
  const paths = [
    ...momcs.map((momc) => momc.url),
    ...orps.map((orp) => orp.url),
    ...cadasters.map((cadaster) => cadaster.url),
    ...cities.map((city) => city.url),
    ...regions.map((region) => region.url),
  ];

  const requestedURLs = [] as string[];
  for (const path of paths) {
    const cachedResource = state.resource[path];
    if (!cachedResource || cachedResource.queryState === QUERY_STATE.FAIL) {
      requestedURLs.push(path);
    }
  }

  await Promise.all(requestedURLs.map((url) => dispatch(actionResourceLoad(url))));
};

export const actionSyncGeometries = (): ThunkAction => async (dispatch, getState) => {
  await dispatch(actionLoadMissingGeometries());

  const state = getState();
  const { momcs, orps, cadasters, cities, regions } = state.newOrder;
  const paths = [
    ...momcs.map((momc) => momc.url),
    ...orps.map((orp) => orp.url),
    ...cadasters.map((cadaster) => cadaster.url),
    ...cities.map((city) => city.url),
    ...regions.map((region) => region.url),
  ];

  const features = [] as GeoJSONFeature[];

  for (const path of paths) {
    const cachedResource = state.resource[path];
    if (cachedResource && cachedResource.queryState === QUERY_STATE.SUCCESS) {
      features.push((cachedResource.data as any).feature);
    }
  }

  dispatch(actionSetPredefinedAreasFeatures(features));
};

export const actionSetPublishsets =
  (publishsets: OrdersPublishsetEditResponse[]) => async (dispatch: Dispatch) => {
    dispatch({
      type: con.NEW_ORDER_SET_PUBLISHSETS,
      publishsets,
    });
  };

export const actionSetReason = (reason: string) => ({
  type: con.NEW_ORDER_SET_REASON,
  reason,
});

export const actionSetHandlingFee = (handling_fee: boolean) => ({
  type: con.NEW_ORDER_SET_HANDLING_FEE,
  handling_fee,
});

export const actionSetNote = (note: string) => ({
  type: con.NEW_ORDER_SET_NOTE,
  note,
});

export const actionSetOrigin = (origin: OriginEnum | null) => ({
  type: con.NEW_ORDER_SET_ORIGIN,
  origin,
});

export const actionSetId = (id: string) => ({
  type: con.NEW_ORDER_SET_ID,
  id,
});

export const actionSetRequirements = (
  isRequirementPublishset: boolean,
  isRequirementValidRegion: boolean,
  isRequirementAgreement: boolean,
  isRequirementDate: boolean,
) => ({
  type: con.NEW_ORDER_SET_REQUIREMENTS,
  isRequirementPublishset,
  isRequirementValidRegion,
  isRequirementAgreement,
  isRequirementDate,
});

export const actionSetQueryState = (
  state: QUERY_STATE,
  id: number | string = '',
  fieldErrors: ServerErrors | null = null,
) => ({
  type: con.NEW_ORDER_SET_QUERY_STATE,
  queryState: state,
  id,
  fieldErrors,
});

export const actionSetAgreement = (agreement: boolean) =>
  ({
    type: con.NEW_ORDER_SET_AGREEMENT,
    payload: agreement,
  }) as const;

export type AttachmentProps = {
  name: string;
  file?: string;
  id?: number;
  luid?: string;
  created?: string;
};
export const actionInitAttachments = (payload: AttachmentProps[]) => ({
  type: con.NEW_ORDER_INIT_ATTACHMENTS,
  payload,
});

export const actionAddAttachment = (payload: AttachmentProps) => ({
  type: con.NEW_ORDER_ADD_ATTACHMENT,
  payload,
});

/** Nová příloha má jen `luid`, příloha uložené objednávky jen `id` ze serveru. */
export const actionDeleteAttachment = (key: string | number) => ({
  type: con.NEW_ORDER_DELETE_ATTACHMENT,
  payload: { key },
});

export const newOrderClear = () => ({ type: con.NEW_ORDER_CLEAR }) as const;

export const submitOrder =
  (action: any, id: string | null, order: OrderEditRequest) => async (dispatch: Dispatch) => {
    dispatch(actionSetQueryState(QUERY_STATE.WAITING));
    const response = await action({
      query: { id },
      request: order,
    });

    if (response.ok) {
      dispatch(newOrderClear());
      dispatch(actionSetQueryState(QUERY_STATE.SUCCESS, response.data.id));
    } else {
      const uploadErrors = response?.error?.data ?? [{ message: '', code: 'unknown_server_error' }];

      dispatch(actionSetQueryState(QUERY_STATE.FAIL, '', uploadErrors));
    }
    return response;
  };

export const actionPublishsetAddFormat = (payload: PublishsetFormatPayload) => ({
  type: con.NEW_ORDER_PUBLISHSET_ADD_FORMAT,
  payload,
});

export const actionPublishsetRemoveFormat = (payload: PublishsetFormatPayload) => ({
  type: con.NEW_ORDER_PUBLISHSET_REMOVE_FORMAT,
  payload,
});

export const actionPublishsetAddAllFormats = (payload: PublishsetSetAllFormatsPayload) => ({
  type: con.NEW_ORDER_PUBLISHSET_ADD_ALL_FORMATS,
  payload,
});

export const actionPublishsetRemoveAllFormats = (payload: PublishsetRemoveAllFormatsPayload) => ({
  type: con.NEW_ORDER_PUBLISHSET_REMOVE_ALL_FORMATS,
  payload,
});

export const actionSetDataValidity = (order_type: OrderTypeEnum) => ({
  type: con.NEW_ORDER_SET_DATA_VALIDITY,
  order_type,
});

export const actionSetStateDataValidityDate = (
  order_data_state_valid_at: Date | null,
  dateValidityMin: Date | null,
  dateValidityMax: Date | null,
) => ({
  type: con.NEW_ORDER_SET_DATA_VALIDITY_STATE_DATE,
  order_data_state_valid_at,
  dateValidityMin,
  dateValidityMax,
});

export const actionSetChangeDataValidityDateFrom = (
  order_data_changes_from: Date | null,
  dateValidityMin: Date | null,
  dateValidityMax: Date | null,
) => ({
  type: con.NEW_ORDER_SET_DATA_VALIDITY_CHANGE_DATE_FROM,
  order_data_changes_from,
  dateValidityMin,
  dateValidityMax,
});

export const actionSetSelectionType = (payload: OrderSelectionTypesEnum) => ({
  type: con.NEW_ORDER_SET_SELECTION_TYPE,
  payload,
});

export const actionSetIsPublic = (payload: ORDER_IS_PUBLIC) => ({
  type: con.NEW_ORDER_SET_IS_PUBLIC,
  payload,
});

export const actionSetAreaType = (payload: con.ORDER_REGION_TYPES) => ({
  type: con.NEW_ORDER_SET_AREA_TYPE,
  payload,
});

export const actionSetOverLimitArea = (payload: boolean) => ({
  type: con.NEW_ORDER_SET_OVER_LIMIT_AREA,
  payload,
});

export const actionSetIsRequirementDate = (payload: boolean) => ({
  type: con.NEW_ORDER_SET_IS_REQUIREMENT_DATE,
  payload,
});

export const actionClearAreas = (): ThunkAction => async (dispatch, _) => {
  batch('actionClearAreas', () => {
    dispatch(actionSetCadasters([]));
    dispatch(actionSetCities([]));
    dispatch(actionSetORPs([]));
    dispatch(actionSetRegions([]));
    dispatch(actionSetMomcs([]));
    dispatch(actionDeleteAllFeatures(UNMANAGED_AREA_LAYERS.CUSTOM_AREAS));
  });
};

export const actionSetPrimaryDB = (payload: boolean) => ({
  type: con.NEW_ORDER_SET_PRIMARY_DB,
  payload,
});
